import React, { createContext, useCallback, useContext, useState } from "react";
import { http } from "./api";
import CheckoutModal from "./components/CheckoutModal";

// Feature keys — mirror plans.py
export const FEATURES = {
  GMAIL: "gmail_integration",
  PROF_EXTRACT: "professor_email_extraction",
  PROF_DRAFT: "professor_email_draft",
  FIT_SCORE: "fit_score",
  ADMIT_PROB: "admission_probability",
  AID_GAP: "aid_gap_analysis",
  SCHOL_MATCHING: "scholarship_matching",
  AI_TODO: "ai_todo_generation",
  FULL_PROFILE: "full_profile",
  UNI_BOARD: "university_board",
  AI_ADVISOR: "ai_advisor",
};

// Copy for each feature surfaced inside the upgrade modal.
const FEATURE_COPY = {
  [FEATURES.GMAIL]: {
    headline: "Unlock Gmail outreach.",
    subhead: "Connect Gmail to draft, save, and send professor emails from inside Vidhya — every reply tracked next to the application.",
  },
  [FEATURES.PROF_EXTRACT]: {
    headline: "Find the right professors.",
    subhead: "Pull faculty emails, labs, and research areas for every university on your board.",
  },
  [FEATURES.PROF_DRAFT]: {
    headline: "Draft outreach in seconds.",
    subhead: "AI writes a personalised cold email to a specific professor, grounded in your background.",
  },
  [FEATURES.FIT_SCORE]: {
    headline: "See your fit score.",
    subhead: "A profile-aware match score for every university — so you know which schools are realistic.",
  },
  [FEATURES.ADMIT_PROB]: {
    headline: "Get honest admission odds.",
    subhead: "Calibrated against the school's published acceptance rate and your specific profile.",
  },
  [FEATURES.AID_GAP]: {
    headline: "Know what you'll actually pay.",
    subhead: "Tuition + living minus your budget minus likely internal aid — see exactly what scholarships need to close.",
  },
  [FEATURES.SCHOL_MATCHING]: {
    headline: "Personalised scholarship matches.",
    subhead: "Search across govt, foundation and university funds tuned to your citizenship, field, and profile.",
  },
  [FEATURES.AI_TODO]: {
    headline: "Get a prioritised to-do list.",
    subhead: "Vidhya plans the next 7 days for you, ordered by urgency and deadline.",
  },
  [FEATURES.FULL_PROFILE]: {
    headline: "Unlock your full profile.",
    subhead: "Every advisor reply gets sharper as your profile fills in — tests, story, leadership, all of it.",
  },
  [FEATURES.UNI_BOARD]: {
    headline: "Track every university.",
    subhead: "Explorer tracks one. Navigator tracks every school you're applying to — no caps, no compromises.",
  },
  [FEATURES.AI_ADVISOR]: {
    headline: "Unlimited advisor.",
    subhead: "Daily message caps disappear. Brainstorm, draft, plan, and revise — whenever you need to.",
  },
};

// Default top-3 perks under the headline feature in the modal.
const TOP_VALUE_PROPS = [
  "Unlimited universities + advisor messages",
  "Fit score, admission odds, aid gap analysis",
  "Gmail outreach + AI scholarship matching",
];

const UpgradeCtx = createContext({
  open: () => {},
  close: () => {},
  isOpen: false,
});

export function UpgradeProvider({ children }) {
  const [state, setState] = useState({
    open: false,
    feature: "",
    context: "",
  });

  const open = useCallback((feature, context) => {
    setState({ open: true, feature: feature || "", context: context || "" });
    // Fire analytics event (best-effort).
    try {
      http.post("/analytics/event", {
        event: "upgrade_modal_shown",
        properties: { feature: feature || "", context: context || "" },
      }).catch(() => {});
    } catch (e) { /* ignore */ }
  }, []);

  const close = useCallback((reason) => {
    setState((s) => {
      if (s.open) {
        try {
          http.post("/analytics/event", {
            event: "upgrade_modal_dismissed",
            properties: { feature: s.feature, context: s.context, reason: reason || "dismiss" },
          }).catch(() => {});
        } catch (e) { /* ignore */ }
      }
      return { open: false, feature: "", context: "" };
    });
  }, []);

  const copy = FEATURE_COPY[state.feature] || {
    headline: "Unlock Navigator.",
    subhead: "Everything you need to actually apply — no caps, no locks.",
  };

  return (
    <UpgradeCtx.Provider value={{ open, close, isOpen: state.open, feature: state.feature, context: state.context }}>
      {children}
      <CheckoutModal
        open={state.open}
        onClose={close}
        feature={state.feature}
        context={state.context}
        headline={copy.headline}
        subhead={copy.subhead}
        valueProps={TOP_VALUE_PROPS}
      />
    </UpgradeCtx.Provider>
  );
}

export function useUpgrade() {
  return useContext(UpgradeCtx);
}

// Convenience function so any caller can trigger the upgrade flow without
// drilling into the hook. When Stripe checkout ships, swap this implementation
// to call the checkout endpoint instead.
export function triggerUpgradeFlow(open, feature, context) {
  open(feature, context);
}

// Detect plan-required errors coming back from gated routes. Returns
// { feature, context, message } when matched, otherwise null.
export function readPlanError(err) {
  const d = err?.response?.data?.detail;
  if (d && typeof d === "object" && d.error === "plan_required") {
    return {
      feature: d.feature || "",
      context: d.context || "",
      message: d.message || "",
      plan: d.plan || "",
      extra: d,
    };
  }
  return null;
}
